import type { ChangeRequest, ProjectSkillState } from '../../../services/api';

export type ChangeWorkspaceSection = 'all' | 'governance' | 'execution' | 'verification';

export const GOVERNANCE_SKILL_IDS = [
  'db-impact',
  'license',
  'config-drift',
  'change-failure',
  'sdlc-continuity',
  'business-forecast',
] as const;

export const EXECUTION_SKILL_IDS = ['legacy-intel', 'digital-twin', 'doc-maintenance'] as const;

export const VERIFICATION_SKILL_IDS = ['business-forecast', 'change-failure', 'db-impact'] as const;

export type VerificationGateStatus = 'verified' | 'pending' | 'blocked' | 'skipped';

export function scopeDatastoreNames(scope?: Record<string, unknown>): string[] {
  const raw = scope?.datastore_names;
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  return [];
}

export function scopeConfigPaths(scope?: Record<string, unknown>): string[] {
  const raw = scope?.config_paths;
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  return [];
}

export function scopeIncidentId(scope?: Record<string, unknown>): string | undefined {
  const id = scope?.incident_id;
  return typeof id === 'string' && id ? id : undefined;
}

export function scopeInsightId(scope?: Record<string, unknown>): string | undefined {
  const id = scope?.insight_id;
  return typeof id === 'string' && id ? id : undefined;
}

export function filterSkills(skills: ProjectSkillState[], ids: readonly string[]): ProjectSkillState[] {
  const set = new Set(ids);
  return skills.filter((s) => set.has(s.id));
}

export function verificationGateStatus(skill: ProjectSkillState): VerificationGateStatus {
  const check = skill.check_result;
  const status = skill.status_in_context ?? skill.status;
  if (check === 'pass' || status === 'ready') return 'verified';
  if (check === 'fail' || status === 'failed') return 'blocked';
  if (status === 'unavailable' || status === 'locked_preview') return 'skipped';
  return 'pending';
}

export function blockingReason(skill: ProjectSkillState): string | undefined {
  if (skill.locked_message) return skill.locked_message;
  const check = skill.check_result;
  const status = skill.status_in_context ?? skill.status;
  if (check === 'fail') return skill.summary_text || 'Check failed — review evidence and scope.';
  if (check === 'warn') return skill.summary_text || 'Warning — review before approving execution.';
  if (status === 'pending' || check === 'pending') {
    return skill.summary_text || 'Waiting for prerequisites or skill execution.';
  }
  if (status === 'running') return 'Check is running…';
  return undefined;
}

export function requiredAction(skill: ProjectSkillState, canRunMoksha: boolean): string {
  const check = skill.check_result;
  const status = skill.status_in_context ?? skill.status;
  if (canRunMoksha && (check === 'fail' || check === 'warn' || check === 'pending')) {
    return 'Run governance check';
  }
  if (status === 'locked_preview') return 'Complete Discover handoff and inventory';
  if (status === 'unavailable') return 'Upload repository and build inventory';
  if (check === 'pass') return 'No action required';
  return 'Review supporting evidence';
}

export function crDisplayTitle(cr: ChangeRequest): string {
  return cr.title || cr.external_ref || cr.id.slice(0, 8);
}
